'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { X, AlertTriangle, Send, Check, RefreshCw, Copy } from 'lucide-react'
import { Button } from './Button'
import { Textarea } from './Textarea'
import { useFeedbackStore } from '../stores/feedbackStore'
import {
  appIdFromPath,
  buildClipboardText,
  buildErrorTicket,
} from '../lib/errorReport/errorReport'
import { uploadScreenshot } from '../lib/errorReport/uploadScreenshot'
import { copyToClipboard } from '../lib/clipboard'

export function ErrorReportModal() {
  const { isErrorReportOpen, errorContext, closeErrorReport } = useFeedbackStore()
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const screenshot = errorContext?.screenshot ?? null

  // Object URL for the thumbnail preview, revoked when the shot changes or the
  // modal unmounts.
  useEffect(() => {
    if (!screenshot) {
      setScreenshotUrl(null)
      return
    }
    const url = URL.createObjectURL(screenshot)
    setScreenshotUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [screenshot])

  if (!isErrorReportOpen || !errorContext) return null

  const { report } = errorContext
  const url =
    errorContext.url || (typeof window !== 'undefined' ? window.location.href : undefined)
  const timestamp = errorContext.timestamp || new Date().toISOString()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const screenshotPath =
        screenshot && includeScreenshot ? await uploadScreenshot(screenshot) : null

      const pathname = typeof window !== 'undefined' ? window.location.pathname : null
      const ticket = buildErrorTicket({
        report,
        userNote: additionalInfo,
        url,
        appId: appIdFromPath(pathname),
        buildId: process.env.NEXT_PUBLIC_BUILD_ID,
        timestamp,
      })

      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...ticket,
          screenshotPath,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || 'Failed to submit the report')
      }

      setSubmitted(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to submit the report. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setAdditionalInfo('')
    setIncludeScreenshot(true)
    setSubmitted(false)
    setCopied(false)
    setError(null)
    closeErrorReport()
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleCopyError = async () => {
    const ok = await copyToClipboard(buildClipboardText({ report, url, timestamp }))
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" data-screenshot-exclude>
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Error report submitted
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Thank you for helping us fix this issue. You can follow it under{' '}
              <Link
                href="/support"
                onClick={handleClose}
                className="font-medium text-blue-600 hover:text-blue-700"
              >
                My requests
              </Link>
              .
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="secondary" onClick={handleClose}>
                Done
              </Button>
              <Button onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-screenshot-exclude>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
              <p className="text-sm text-gray-600">We&apos;d appreciate if you could report this error</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
          {/* Error Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Error Details
              </span>
              <button
                type="button"
                onClick={handleCopyError}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy stack trace
                  </>
                )}
              </button>
            </div>
            <p className="text-sm font-medium text-red-600 break-words">
              {report.message}
            </p>
            <p className="text-xs text-gray-500">
              {url} • {new Date(timestamp).toLocaleString()}
            </p>
          </div>

          {/* Screenshot preview */}
          {screenshotUrl && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(e) => setIncludeScreenshot(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Include a screenshot of the page
              </label>
              {includeScreenshot && (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={screenshotUrl}
                    alt="Screenshot that will be attached to the report"
                    className="max-h-40 rounded-lg border border-gray-200 object-contain"
                  />
                  <p className="text-xs text-gray-500">
                    This shows everything that was on the page, so check it for anything
                    personal before sending. Only you and support can see it.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Additional Context */}
          <Textarea
            label="What were you doing when this happened? (optional)"
            placeholder="E.g., I was trying to log a feed in the baby tracker when..."
            rows={3}
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            error={error || undefined}
          />
          <p className="text-xs text-gray-500 -mt-3">
            This helps us understand and reproduce the issue faster.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleRefresh}
              className="sm:flex-1"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Just Refresh
            </Button>
            <Button type="submit" disabled={loading} className="sm:flex-1">
              {loading ? (
                'Submitting...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
